/**
 * Welcome-step content map (ADR-076 §4 L5).
 *
 * The shared `welcome` step renderer is used by several journeys (sign-up,
 * manual-onboard-*, add-second-domain, manage-account) that each need different
 * copy. Per L5, per-journey copy is DATA, not `if (journeyId === …)` branching
 * inside the shared renderer: StepWelcome does a pure lookup into this map keyed
 * by journeyId (mirrors content/bow.ts). Adding/tailoring a journey's welcome
 * copy is editing this map — never re-introducing a branch in the component.
 */

export interface WelcomeStepPreviewItem {
  num: number;
  label: string;
  description: string;
}

export interface WelcomeContent {
  heading: string;
  body: string;
  steps: WelcomeStepPreviewItem[];
  /** sign-up only — the plan/payment/domain "required to activate" notice. */
  showRequiredNotice: boolean;
  /** show the "~10–15 minutes, pause and resume" footnote. */
  showTimeFootnote: boolean;
}

const SIGNUP_STEPS: WelcomeStepPreviewItem[] = [
  { num: 1, label: 'Authorize your domain', description: 'Connect the website you want Nexanomics to monitor.' },
  { num: 2, label: 'Choose a plan', description: 'Pick the tier that matches your traffic and team size.' },
  { num: 3, label: 'Set up payment', description: 'Add a card to activate your subscription.' },
  { num: 4, label: 'Tell us about your business', description: 'Profile, persona, brand — used to personalise your dashboard.' },
  { num: 5, label: 'Install the snippet', description: 'A single line of HTML — and we light up your trust signals.' },
];

const ADMIN_STEPS: WelcomeStepPreviewItem[] = [
  { num: 1, label: "Authorize the subscriber's domain", description: "Confirm authorisation on the subscriber's behalf." },
  { num: 2, label: 'Capture business profile', description: 'Profile, persona, brand — for their dashboard.' },
  { num: 3, label: 'Handover', description: 'Subscriber completes plan, payment, and snippet on first sign-in.' },
];

const ADD_DOMAIN_STEPS: WelcomeStepPreviewItem[] = [
  { num: 1, label: 'Authorize the new domain', description: 'Confirm you own or have authority over this website.' },
  { num: 2, label: 'Build the per-domain profile', description: 'Persona and brand for the new domain (your other domains are unaffected).' },
  { num: 3, label: 'Install the snippet', description: 'Drop the snippet on the new site to start ingesting signals.' },
];

const SIGNUP: WelcomeContent = {
  heading: 'Welcome to Nexanomics',
  body: "Let's get your account set up. This wizard walks you through connecting your first domain, choosing a plan, and installing your trust snippet.",
  steps: SIGNUP_STEPS,
  showRequiredNotice: true,
  showTimeFootnote: true,
};

const ADMIN: WelcomeContent = {
  heading: 'Onboarding on behalf of subscriber',
  body: "You're running this onboarding workflow on behalf of a subscriber. Complete each step to set up their account profile.",
  steps: ADMIN_STEPS,
  showRequiredNotice: false,
  showTimeFootnote: false,
};

const ADD_DOMAIN: WelcomeContent = {
  heading: 'Add a second domain',
  body: "Let's connect another domain to your account. You'll authorise the domain, build a per-domain profile, and install the snippet.",
  steps: ADD_DOMAIN_STEPS,
  showRequiredNotice: false,
  showTimeFootnote: true,
};

const MANAGE_ACCOUNT: WelcomeContent = {
  heading: 'Complete your account setup',
  body: 'Pick up where you left off. All steps are optional — you can save progress at any time.',
  steps: [],
  showRequiredNotice: false,
  showTimeFootnote: false,
};

/** Keyed by journeyId. `default` covers any journey not explicitly listed. */
export const WELCOME_CONTENT: Record<string, WelcomeContent> = {
  'sign-up': SIGNUP,
  'manual-onboard-no-plan': ADMIN,
  'manual-onboard-with-plan': ADMIN,
  'add-second-domain': ADD_DOMAIN,
  'manage-account': MANAGE_ACCOUNT,
  default: SIGNUP,
};
